import Page from '../../components/shared/Page/Page';
import SecondaryFooter from '../../components/shared/SecondaryFooter/SecondaryFooter';
import StyledContentLayout from '../../styles/StyledContentLayout';
import useAuth from '../../hooks/useAuth';

const PrivacyPolicy: React.FC = () => {
  const { currentUser } = useAuth();

  return (
    <Page title="Elora | Privacy Policy">
      <StyledContentLayout>
        <h1>Privacy Policy</h1>

        <p>
          ELORA is committed to protecting the personal data of its Users in
          accordance with the EU General Data Protection Regulation (GDPR) and
          applicable laws. This Privacy Policy explains how we collect, use,
          disclose, and safeguard your information when you visit our website
          elora.lu. Please read this privacy policy carefully. If you do not
          agree with the terms of this privacy policy, please do not access the
          site.
        </p>

        <h2>
          <strong>1. Collected Information</strong>
        </h2>

        <p>
          In the course of providing the Services, ELORA may collect and process
          certain data and other personal information about the User in a
          variety of ways.
        </p>

        <p>This information may include:</p>

        <ul>
          <li>
            Identification and contact details (personal name, company name,
            username, email address, phone numbers), including details that you
            voluntarily give to us when you register on the site, place an
            order, or subscribe to our newsletter;
          </li>
          <li>
            Content you submit or that is generated through the Service: e.g.,
            messages or questions you send to the chat agent, audio recordings
            or transcripts from the call agent, and any files or materials you
            upload;
          </li>
          <li>
            Usage data (such as pages visited, features used, and technical
            logs) and other information our servers automatically collect when
            you access the Website, such as your IP address, browser type,
            operating system, access times;
          </li>
          <li>
            Payment Information: to use the services you will need to provide us
            payment information, including your credit or debit card number,
            card expiration date, CVV code, and billing address;
          </li>
          <li>
            Preferences: when you set your preferences using your settings, we
            collect that information so that we can respect your preferences;
          </li>
          <li>
            Mobile Device Data, namely device information, such as your mobile
            device ID, model, and manufacturer, and information about the
            location of your device, if you access the Website or our services
            from a mobile device.
          </li>
        </ul>

        <h2>
          <strong>2. Use of Data</strong>
        </h2>

        <p>
          The data collected is used strictly for internal purposes and only to
          the extent necessary to provide and enhance the functionalities of the
          Services.
        </p>

        <p>
          ELORA uses this information solely for specified purposes, which
          include:
        </p>

        <ul>
          <li>Creating and managing your (company&#39;s) account;</li>
          <li>Providing and personalizing the Services for the User;</li>
          <li>
            Answering the User&#39;s queries and generating call summaries;
          </li>
          <li>Authenticating and managing the User&#39;s Account;</li>
          <li>
            Providing customer support and technical assistance and analysing
            usage of the Services to improve its features and performance;
          </li>
          <li>Improving our website and customer service;</li>
          <li>
            Monitoring and analysing usage and trends to improve your experience
            with the Website;
          </li>
          <li>Sending you a newsletter or other marketing communications.</li>
        </ul>

        <p>
          All collection and use of personal data by ELORA is carried out in
          accordance with applicable data protection laws (including the GDPR).
        </p>

        <p>
          In addition to the information actively provided by the User, ELORA
          may collect technical and usage-related data automatically when the
          User accesses the Services. This may include information such as the
          User&#39;s IP address, browser type and version, pages visited on the
          platform, the time and date of access, time spent on specific pages,
          and other diagnostic data.
        </p>

        <h2>
          <strong>3. Disclosure of Data</strong>
        </h2>

        <p>
          Your information may be disclosed if we believe the release of
          information about you is necessary to respond to legal process, to
          investigate or remedy potential violations of our policies, or to
          protect the rights, property, and safety of others, we may share your
          information as permitted or required by any applicable law, rule, or
          regulation.
        </p>

        <p>
          ELORA may share data with trusted third-party service providers who
          support the operation of the Services, help manage certain business
          functions, or assist in delivering support to Users. These providers
          act on behalf of ELORA and are contractually bound to process data
          solely for the purposes defined by ELORA, and not for their own use.
          They are also required to maintain the confidentiality and security of
          the information provided. ELORA does not share data with third parties
          unless it is strictly necessary for Services provision or required by
          law.
        </p>

        <p>
          Personal data may also be disclosed to hosting providers, as well as
          in the event of a legal obligation, a security incident, or a
          corporate transaction such as a merger or acquisition.
        </p>

        <p>
          All of your data is stored within the European Union. ELORA will not
          transfer your personal data outside the European Union unless such
          transfer is compliant with applicable data protection laws (for
          instance, if you have explicitly requested it, or if it is necessary
          for the Service and appropriate safeguards are in place).
        </p>

        <h2>
          <strong>4. Security of Data</strong>
        </h2>

        <p>
          ELORA takes appropriate administrative, technical, physical security
          and organizational measures to safeguard personal data against
          unauthorized access, alteration, disclosure, or destruction. In
          particular, all personal data is encrypted during transfer (using TLS
          1.2 or higher) and while at rest on ELORA&#39;s systems (using AES-256
          encryption). Data is backed up daily and those backups are retained
          for 365 days, ensuring the ability to restore data if needed.
        </p>

        <p>
          ELORA cannot guarantee absolute security of your data; however, ELORA
          continually reviews and updates its security measures to mitigate new
          threats and vulnerabilities.
        </p>

        <h2>
          <strong>5. Professional Use Only - Policy for Children</strong>
        </h2>

        <p>
          Our website and ELORA Services are intended for professional use only.
        </p>

        <p>
          We do not knowingly solicit information from or market to children
          under the age of 18. If we learn that we have collected personal
          information from a child under age 18 without verification of parental
          consent, we will delete that information as quickly as possible.
        </p>

        <h2>
          <strong>6. Data Subject Requests</strong>
        </h2>

        <p>Under GDPR, you have various rights, including:</p>

        <ul>
          <li>
            The right to access, update or delete the information we have on
            you;
          </li>
          <li>The right of rectification;</li>
          <li>The right to object;</li>
          <li>The right of restriction;</li>
          <li>The right to data portability;</li>
          <li>The right to withdraw consent.</li>
        </ul>

        <p>
          You can access, correct, or modify the information you provided to us
          by editing and adjusting your account settings.
        </p>

        <p>
          To learn more about the information we have collected, request access
          to additional information per email.
        </p>

        <p>
          To protect your privacy and maintain security, we take steps to verify
          your identity before granting you access to your personal information
          or complying with a deletion, portability, or other related request.
          We may, in certain situations, reject your request for access,
          correction, or portability, for example, we may reject access where
          you are unable to verify your identity.
        </p>

        <h2>
          <strong>7. Changes to this Privacy Policy</strong>
        </h2>

        <p>
          We may update this privacy policy from time to time in order to
          reflect changes to our practices or for other operational, legal, or
          regulatory reasons. We will notify you of any changes by posting the
          new Privacy Policy on the Website. You are advised to review this
          Privacy Policy periodically for any changes.
        </p>

        <h2>
          <strong>8. Contact Information</strong>
        </h2>

        <p>
          Users may request access to their personal data at any time. Requests
          can be sent by email to: <strong>GDPR@elora.lu</strong>.
        </p>
      </StyledContentLayout>
      {!currentUser && <SecondaryFooter />}
    </Page>
  );
};

export default PrivacyPolicy;
